/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";
import axios from "axios";

// react-router-dom components
import { useLocation, NavLink, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import MuiLink from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Collapse from "@mui/material/Collapse";
import { Link as RouterLink } from "react-router-dom";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import { getStoredSession, hydrateStoredSession } from "utils/userIdentity";
import { farmsForUserUrl, hatcheryDispatchUrl } from "api/endpoints";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

const unwrapCollection = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.value)) return payload.value;
  if (Array.isArray(payload?.result)) return payload.result;
  return [];
};

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const navigate = useNavigate();
  const collapseName = location.pathname.replace("/", "");
  const session = getStoredSession();
  const userTypeID = Number(session?.userTypeID);
  const [openMenus, setOpenMenus] = useState(() => ({
    reports: location.pathname.startsWith("/reports"),
  }));
  const [hasOutstandingDocs, setHasOutstandingDocs] = useState(false);

  useEffect(() => {
    let active = true;
    const loadOutstandingDocs = async () => {
      try {
        const currentSession = getStoredSession() ?? (await hydrateStoredSession());
        const userId =
          currentSession?.ID ??
          currentSession?.id ??
          currentSession?.userID ??
          currentSession?.userId ??
          currentSession?.UserID;
        if (userId == null) return;
        const farmsResponse = await axios.get(farmsForUserUrl(userId));
        const farms = unwrapCollection(farmsResponse.data);
        const farmIds = [
          ...new Set(
            farms.map(
              (farm) => farm?.farmId ?? farm?.FarmID ?? farm?.farmID ?? farm?.id ?? farm?.ID
            )
          ),
        ].filter((id) => id != null);
        const results = await Promise.allSettled(
          farmIds.map((farmId) => axios.get(hatcheryDispatchUrl(farmId)))
        );
        if (!active) return;
        setHasOutstandingDocs(
          results.some((result) => {
            if (result.status !== "fulfilled") return false;
            const dispatches = unwrapCollection(result.value.data);
            return dispatches.some((dispatch) =>
              [1, 2, 3].includes(Number(dispatch?.dispatchStatus))
            );
          })
        );
      } catch {
        if (active) setHasOutstandingDocs(false);
      }
    };
    loadOutstandingDocs();
    window.addEventListener("doc-transactions-updated", loadOutstandingDocs);
    return () => {
      active = false;
      window.removeEventListener("doc-transactions-updated", loadOutstandingDocs);
    };
  }, [location.pathname]);

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    ({
      type,
      name,
      icon,
      title,
      noCollapse,
      key,
      href,
      route,
      onClick,
      visibleForUserTypeIDs,
      collapse,
    }) => {
      let returnValue;

      if (visibleForUserTypeIDs && !visibleForUserTypeIDs.includes(userTypeID)) {
        return null;
      }

      if (type === "collapse") {
        if (collapse?.length) {
          const isOpen = Boolean(openMenus[key]);
          const childIsActive = collapse.some((child) => child.route === location.pathname);

          returnValue = (
            <MDBox key={key}>
              <MDBox
                component="button"
                type="button"
                aria-expanded={isOpen}
                aria-controls={`${key}-submenu`}
                onClick={() => setOpenMenus((current) => ({ ...current, [key]: !isOpen }))}
                sx={{
                  display: "block",
                  width: "100%",
                  p: 0,
                  border: 0,
                  color: "inherit",
                  background: "transparent",
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <SidenavCollapse
                  name={name}
                  icon={icon}
                  active={childIsActive}
                  hasChildren
                  open={isOpen}
                />
              </MDBox>
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List id={`${key}-submenu`} component="div" disablePadding>
                  {collapse.map((child) => (
                    <NavLink
                      key={child.key}
                      to={child.route}
                      onClick={() => {
                        if (window.innerWidth < 1200) closeSidenav();
                      }}
                    >
                      <SidenavCollapse
                        name={child.name}
                        icon={child.icon}
                        active={child.route === location.pathname}
                        nested
                        indicator={child.key === "transactions" && hasOutstandingDocs}
                      />
                    </NavLink>
                  ))}
                </List>
              </Collapse>
            </MDBox>
          );
        } else if (href) {
          returnValue = (
            <MuiLink
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                active={key === collapseName}
                noCollapse={noCollapse}
              />
            </MuiLink>
          );
        } else {
          const handleNavClick = (event) => {
            if (typeof onClick === "function") {
              let target;
              try {
                target = onClick();
              } catch (error) {
                console.warn("Sidenav route handler failed", error);
              }
              if (target === false) {
                event.preventDefault();
                return;
              }
              if (typeof target === "string" && target.length > 0) {
                if (target !== route) {
                  event.preventDefault();
                  navigate(target);
                }
                closeSidenav();
                return;
              }
            }
            closeSidenav();
          };

          returnValue = (
            <NavLink key={key} to={route} onClick={handleNavClick}>
              <SidenavCollapse name={name} icon={icon} active={key === collapseName} />
            </NavLink>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
            sx={{ my: 1 }}
          />
        );
      }

      return returnValue;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox
          component={NavLink}
          to="/"
          display="flex"
          justifyContent="center"
          alignItems="center"
          px={3}
          py={1}
        >
          {brand && (
            <RouterLink to="/facilities" style={{ display: "inline-block" }}>
              <MDBox
                component="img"
                src={brand}
                alt="OmnioFarm"
                sx={{ width: "160px", height: "auto", objectFit: "contain", cursor: "pointer" }}
              />
            </RouterLink>
          )}
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
        sx={{ my: 2 }}
      />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
